/**
 * CLIController - Developer/Admin Command Line Interface
 *
 * Provides a command-line interface within the chat prompt for developers
 * and administrators to configure the SaaS client behavior.
 *
 * Commands are prefixed with "::" and only available to users with role > 50.
 *
 * Usage:
 *   ::help - Show available commands
 *   ::clear - Clear chat history
 *   ::debug on - Enable debug mode
 *   ::feature list - List feature flags
 *   ::model status - Show LLM model status
 *
 * Singleton pattern - use CLIController.method() to access
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';
import { EntityClasses, getRoleByName } from '../../../../packages/shared/src/metadata/entityClasses.js';
import { createTurn, ROLE, SOURCE, SINK } from '../../../../shared/conversation.js';
import {
  listTemplates,
  getTemplate,
  toResponseSchema,
  toRoutingGrammar,
  routingPrompt,
  esc,
  NO_TEMPLATE
} from '../../../../shared/meta/meta_templates.js';

class CLIController {
  // Minimum role value required for CLI access
  static MIN_ROLE_VALUE = 50;

  // Available commands
  static COMMANDS = {
    help: 'Show available commands',
    clear: 'Clear chat canvas',
    debug: 'Toggle debug mode (::debug on|off)',
    version: 'Show application version',
    feature: 'Feature flag operations (::feature list|get|set)',
    model: 'LLM model operations (::model status|download|clear)',
    reload: 'Reload the page',
    export: 'Export chat history as JSON',
    theme: 'Toggle theme (::theme dark|light)',
    auth: 'Show authentication status',
    template: 'Template registry (::template list|show|schema|render)'
  };

  /**
   * Check if user has CLI access based on role
   * @param {Object} entity - User entity from AuthController
   * @returns {boolean}
   */
  static hasAccess(entity) {
    if (!entity || !entity.class) {
      return false;
    }

    // Get role value from class name
    let roleValue = 0;

    if (typeof entity.class === 'string') {
      // class is a string like "admin_root_role"
      const role = getRoleByName(entity.class);
      roleValue = role ? role.value : 0;
    } else if (typeof entity.class === 'object' && entity.class.value !== undefined) {
      // class is already an object with value
      roleValue = entity.class.value;
    }

    console.log(`[CLIController] Role check: ${entity.class} → value: ${roleValue}`);
    return roleValue >= this.MIN_ROLE_VALUE;
  }

  /**
   * Process CLI command
   * @param {string} input - Full command input (including ::)
   * @param {Object} entity - User entity from AuthController
   * @returns {Object} Response object with message and action
   */
  static async process(input, entity) {
    // Check access
    if (!this.hasAccess(entity)) {
      return {
        error: true,
        message: '⛔ **Access Denied**\n\nCLI commands require admin privileges (role ≥ 50).'
      };
    }

    // Remove :: prefix and parse command
    const command = input.slice(2).trim();
    const [cmd, ...args] = command.split(/\s+/);

    console.log('[CLIController] Processing command:', cmd, 'Args:', args);

    // Route to command handler
    switch (cmd.toLowerCase()) {
      case 'help':
        return this.cmdHelp();
      case 'clear':
        return this.cmdClear();
      case 'debug':
        return this.cmdDebug(args);
      case 'version':
        return this.cmdVersion();
      case 'feature':
        return this.cmdFeature(args);
      case 'model':
        return this.cmdModel(args);
      case 'reload':
        return this.cmdReload();
      case 'export':
        return this.cmdExport();
      case 'theme':
        return this.cmdTheme(args);
      case 'auth':
        return this.cmdAuth(entity);
      case 'template':
        return this.cmdTemplate(args);
      default:
        return {
          error: true,
          message: `❌ **Unknown command:** \`${cmd}\`\n\nType \`::help\` for available commands.`
        };
    }
  }

  /**
   * Display help
   */
  static cmdHelp() {
    let help = '📖 **CLI Commands**\n\n';
    help += 'Available commands (prefix with `::`)\n\n';

    for (const [cmd, desc] of Object.entries(this.COMMANDS)) {
      help += `• \`::${cmd}\` - ${desc}\n`;
    }

    help += '\n**Examples:**\n';
    help += '• `::debug on` - Enable debug logging\n';
    help += '• `::feature list` - Show all feature flags\n';
    help += '• `::model status` - Check LLM model status\n';

    return { message: help };
  }

  /**
   * Clear chat canvas AND the model transcript.
   *
   * These are two separate memories. Wiping only the canvas leaves the model
   * still holding the entire conversation, so it answers from history the user
   * can no longer see - which reads as the model inventing context.
   */
  static cmdClear() {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) {
      return { error: true, message: '❌ Failed to clear canvas' };
    }

    chatMessages.innerHTML = '';

    const hadTranscript = window.LLMController?.history?.length ?? 0;
    window.LLMController?.resetConversation();

    return {
      message: `✅ Chat canvas cleared${hadTranscript ? ` (and ${hadTranscript} transcript messages)` : ''}`
    };
  }

  /**
   * Toggle debug mode
   */
  static cmdDebug(args) {
    const action = args[0]?.toLowerCase();

    if (!action || !['on', 'off'].includes(action)) {
      const currentState = localStorage.getItem('debug_mode') === 'true';
      return {
        message: `🐛 **Debug Mode**\n\nCurrent state: \`${currentState ? 'ON' : 'OFF'}\`\n\nUsage: \`::debug on|off\``
      };
    }

    const enable = action === 'on';
    localStorage.setItem('debug_mode', enable);

    return {
      message: `🐛 Debug mode ${enable ? '**enabled**' : '**disabled**'}`
    };
  }

  /**
   * Show version
   */
  static cmdVersion() {
    return {
      message: `ℹ️ **StreamSyncEngage SaaS Client**\n\nVersion: 1.0.0\nBuild: ${import.meta.env.MODE}\nNode Env: ${import.meta.env.MODE}`
    };
  }

  /**
   * Feature flag operations
   */
  static cmdFeature(args) {
    const action = args[0]?.toLowerCase();

    if (!action) {
      return {
        message: '🚩 **Feature Flags**\n\nUsage:\n• `::feature list` - List all flags\n• `::feature get <name>` - Get flag value\n• `::feature set <name> <value>` - Set flag value'
      };
    }

    switch (action) {
      case 'list': {
        let msg = '🚩 **Feature Flags**\n\n';
        const flags = Object.entries(FEATURE_FLAGS).slice(0, 20); // Show first 20
        for (const [key, value] of flags) {
          msg += `• \`${key}\`: \`${value}\`\n`;
        }
        msg += `\n*Showing 20 of ${Object.keys(FEATURE_FLAGS).length} flags*`;
        return { message: msg };
      }

      case 'get': {
        const flagName = args[1];
        if (!flagName) {
          return { error: true, message: '❌ Flag name required: `::feature get <name>`' };
        }
        const value = FEATURE_FLAGS[flagName];
        if (value === undefined) {
          return { error: true, message: `❌ Flag not found: \`${flagName}\`` };
        }
        return { message: `🚩 \`${flagName}\` = \`${value}\`` };
      }

      case 'set': {
        return {
          message: '⚠️ **Feature flag modification**\n\nSetting flags dynamically is not yet implemented.\nFlags are loaded from `featureFlags.js`.'
        };
      }

      default:
        return { error: true, message: `❌ Unknown action: \`${action}\`` };
    }
  }

  /**
   * LLM model operations
   */
  static cmdModel(args) {
    const action = args[0]?.toLowerCase();

    if (!action) {
      return {
        message: '🤖 **LLM Model Operations**\n\nUsage:\n• `::model status` - Show model status\n• `::model download` - Download model\n• `::model clear` - Clear model cache'
      };
    }

    switch (action) {
      case 'status': {
        if (!window.LLMController) {
          return { error: true, message: '❌ LLMController not available' };
        }

        const status = window.LLMController.getStatus();
        let msg = '🤖 **LLM Model Status**\n\n';
        msg += `• Model: \`${status.modelId}\`\n`;
        msg += `• Loaded: \`${status.isLoaded ? 'YES' : 'NO'}\`\n`;
        msg += `• Cached: \`${status.isCached ? 'YES' : 'NO'}\`\n`;
        msg += `• Generating: \`${status.isGenerating ? 'YES' : 'NO'}\`\n`;
        msg += `• Message Count: \`${status.messageCount}\`\n`;

        return { message: msg };
      }

      case 'download': {
        if (!window.LLMController) {
          return { error: true, message: '❌ LLMController not available' };
        }

        // Trigger download
        window.LLMController.loadModel();
        return { message: '⏳ Starting model download...' };
      }

      case 'clear': {
        return {
          message: '⚠️ **Clear Model Cache**\n\nClearing browser cache requires manual action:\n1. Open DevTools (F12)\n2. Application → Storage → Cache Storage\n3. Delete `webllm/model`'
        };
      }

      default:
        return { error: true, message: `❌ Unknown action: \`${action}\`` };
    }
  }

  /**
   * Reload page
   */
  static cmdReload() {
    setTimeout(() => {
      window.location.reload();
    }, 500);
    return { message: '🔄 Reloading page...' };
  }

  /**
   * Export chat history
   */
  static cmdExport() {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) {
      return { error: true, message: '❌ Chat canvas not found' };
    }

    const bubbles = Array.from(chatMessages.children);
    const history = bubbles.map(bubble => ({
      type: bubble.tagName.toLowerCase().includes('prompt') ? 'user' : 'assistant',
      message: bubble.getAttribute('message'),
      timestamp: bubble.getAttribute('timestamp')
    }));

    const json = JSON.stringify(history, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    return { message: `✅ Exported ${history.length} messages` };
  }

  /**
   * Toggle theme
   */
  static cmdTheme(args) {
    const theme = args[0]?.toLowerCase();

    if (!theme || !['dark', 'light'].includes(theme)) {
      return {
        message: '🎨 **Theme Toggle**\n\nUsage: `::theme dark|light`'
      };
    }

    // Store in localStorage for persistence
    localStorage.setItem('theme', theme);

    return {
      message: `🎨 Theme set to **${theme}**\n\n⚠️ Theme switching not yet implemented in UI.`
    };
  }

  /**
   * Template registry operations.
   *
   * `::template render <ID> [json]` is the model-free trigger path: it drives
   * hydrate -> canvas -> affordance end to end with no routing pass, which is how
   * you build a template before the grammar knows anything about it.
   */
  static async cmdTemplate(args) {
    const action = args[0]?.toLowerCase();

    if (!action) {
      return {
        message: '🧩 **Template Registry**\n\nUsage:\n' +
          '• `::template list` - Registered views\n' +
          '• `::template show <ID>` - Entry detail\n' +
          '• `::template schema` - Grammar the model is constrained to\n' +
          '• `::template prompt` - The classifier system prompt\n' +
          '• `::template test` - Score every labelled routing case\n' +
          '• `::template route "<text>"` - Probe the router without spending a turn\n' +
          '• `::template render <ID> [json]` - Render without the model'
      };
    }

    switch (action) {
      case 'list': {
        const templates = listTemplates();
        if (!templates.length) {
          return { message: '🧩 **Template Registry**\n\n*No templates registered.*' };
        }

        let msg = '🧩 **Template Registry**\n\n';
        for (const def of templates) {
          msg += `• \`${def.id}\` - ${def.when}\n`;
        }
        msg += `\n*${templates.length} registered*`;
        return { message: msg };
      }

      case 'show': {
        const id = args[1];
        if (!id) return { error: true, message: '❌ Template id required: `::template show <ID>`' };

        const def = getTemplate(id);
        if (!def) return { error: true, message: `❌ Not registered: \`${id}\`` };

        let msg = `🧩 **${def.id}**\n\n`;
        msg += `• When: ${def.when}\n`;
        msg += `• Params: \`${JSON.stringify(def.params)}\`\n`;
        msg += `• Source: \`${def.source || 'none'}\`\n`;
        msg += `• Fetches: \`${def.fetch ? 'YES' : 'NO'}\`\n`;
        return { message: msg };
      }

      case 'schema': {
        // The grammar is what the router is actually held to; the JSON schema is
        // kept for the params work and shown second so the two never get confused.
        return {
          message: '🧩 **Routing Grammar** (EBNF - what constrains the decode)\n\n```\n' +
            (toRoutingGrammar() || '(nothing registered)') + '\n```\n\n' +
            '**JSON Schema** (unused by routing; for param extraction)\n\n```json\n' +
            JSON.stringify(toResponseSchema(), null, 2) + '\n```'
        };
      }

      case 'prompt': {
        const prompt = routingPrompt();
        return {
          message: prompt
            ? '🧩 **Routing Prompt**\n\n```\n' + prompt + '\n```'
            : '🧩 *No templates registered - the routing pass is skipped entirely.*'
        };
      }

      case 'test': {
        // Scores the whole labelled set instead of one manual probe. Pattern hits
        // are free; only the remainder costs a routing pass, so a full run is
        // roughly (cases without a pattern) x one prefill.
        const { ROUTING_CASES } = await import('../templates/routing.cases.js');
        const { matchPatterns } = await import('../../../../shared/meta/meta_templates.js');

        if (!window.LLMController?.getStatus?.().isLoaded) {
          return { error: true, message: '⏳ Model not loaded - pattern layer only. Try again once it is ready.' };
        }

        let pass = 0;
        const fails = [];

        for (const c of ROUTING_CASES) {
          const matched = matchPatterns(c.text);
          let got, via;

          if (matched) {
            got = matched.id; via = 'pattern';
          } else if (c.byPattern) {
            got = '(no pattern)'; via = 'pattern';
          } else {
            const hit = await window.LLMController.route(c.text);
            got = hit ? hit.componentId : '(pass did not run)';
            via = 'model';
          }

          if (got === c.expect) pass++;
          else fails.push(`- \`${esc(c.text)}\`\n  expected **${c.expect}**, got **${got}** (${via})`);
        }

        const total = ROUTING_CASES.length;
        const pct = Math.round((pass / total) * 100);

        return {
          message: `🧪 **Routing** ${pass}/${total} (${pct}%)\n\n` +
            (fails.length ? fails.join('\n') : 'All cases pass.')
        };
      }

      case 'route': {
        // Probe the classifier in isolation. route() does not touch the
        // transcript, so tuning `when` lines here costs nothing but a prefill.
        const text = args.slice(1).join(' ').trim().replace(/^["']|["']$/g, '');
        if (!text) return { error: true, message: '❌ Text required: `::template route "what is a cat"`' };

        if (!window.LLMController?.getStatus?.().isLoaded) {
          return { error: true, message: '⏳ Model not loaded yet - routing is skipped until it is' };
        }

        const hit = await window.LLMController.route(text);
        const last = window.LLMController.lastRouting;

        // The marks are the decision; component_id is only the commit. Showing
        // both is what distinguishes "it judged wrongly" from "it judged right
        // and then committed something else".
        let marks = '';
        if (last?.parsed) {
          const rows = Object.entries(last.parsed)
            .filter(([k]) => k !== 'component_id' && k !== 'params')
            .map(([k, v]) => `${v === true ? '✔' : '·'} ${k}`);
          marks = `\n\n**Marks**\n\`\`\`\n${rows.join('\n')}\n→ committed ${last.committed}\n\`\`\``;
        }

        const outcome = !hit
          ? `→ **(pass did not run)** - model loading or busy; check the console`
          : hit.componentId === NO_TEMPLATE
            ? `→ **${NO_TEMPLATE}** - would decline as out of scope`
            : `→ **${hit.componentId}**\nparams: \`${JSON.stringify(hit.params)}\``;

        return { message: `🧭 \`${esc(text)}\`\n\n${outcome}${marks}` };
      }

      case 'render': {
        const id = args[1];
        if (!id) return { error: true, message: '❌ Template id required: `::template render <ID> [json]`' };
        if (!getTemplate(id)) return { error: true, message: `❌ Not registered: \`${id}\`` };

        let params = {};
        const rest = args.slice(2).join(' ').trim();

        if (rest) {
          try {
            params = JSON.parse(rest);
          } catch (err) {
            return { error: true, message: `❌ Params must be JSON: ${err.message}` };
          }
        }

        PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_WORKFLOW, createTurn({
          role: ROLE.ASSISTANT,
          source: SOURCE.SYSTEM,
          // Required, not decorative: createTurn always populates `sinks`, so the
          // topic leaf never gets a chance to resolve them on the receiving end.
          sinks: [SINK.WORKFLOW],
          directive: { componentId: id, params }
        }));

        return { message: `🧩 Rendering \`${id}\`...` };
      }

      default:
        return { error: true, message: `❌ Unknown action: \`${action}\`` };
    }
  }

  /**
   * Show authentication status
   */
  static cmdAuth(entity) {
    if (!entity) {
      return { error: true, message: '❌ Not authenticated' };
    }

    // Get role details
    let roleDesc = 'Unknown';
    let roleValue = 0;

    if (typeof entity.class === 'string') {
      const role = getRoleByName(entity.class);
      if (role) {
        roleDesc = role.desc;
        roleValue = role.value;
      }
    } else if (typeof entity.class === 'object') {
      roleDesc = entity.class.desc || 'Unknown';
      roleValue = entity.class.value || 0;
    }

    let msg = '🔐 **Authentication Status**\n\n';
    msg += `• Email: \`${entity.email}\`\n`;
    msg += `• Name: \`${entity.first} ${entity.last}\`\n`;
    msg += `• Role: \`${entity.class}\`\n`;
    msg += `• Role Description: \`${roleDesc}\`\n`;
    msg += `• Role Value: \`${roleValue}\`\n`;
    msg += `• Company: \`${entity.company || 'N/A'}\`\n`;
    msg += `• Status: \`${entity.status || 'Unknown'}\`\n`;

    return { message: msg };
  }
}

export default CLIController;
